import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Color, ErrorMessage, ISelectOption } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import {
    ListInternalContactsForClientRequest,
    ListInternalContactsForClientService,
    SendInternalContactEmailRequest,
    SendInternalContactEmailService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Contact } from '@lsq-frontend/remote-services/enterprise/common';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

export const enum HelpOption {
    InvoiceAndFundingStatus = 1,
    AgingInquiry = 2,
    Pricing = 3,
    TechnicalSupport = 4,
    Other = 5
}

export enum HelpOptionForDisplay {
    'Invoice and Funding Status' = 1,
    'Aging Inquiry' = 2,
    'Pricing' = 3,
    'Technical Support' = 4,
    'Other' = 5
}

@Component({
    selector: 'contact-us',
    template: `
        <lsq-banner [titleText]="title"></lsq-banner>

        <div class="contact-us container">
            <h1 i18n="Heading for the 'need assistance?' section">
                Need Assistance With Dashboard?
            </h1>

            <div class="contact-us-content">
                <div class="contact-us-content-info" *ngIf="internalContact">
                    <div>
                        <h4 i18n="Heading for 'client service' contact information">
                            Client Service
                        </h4>
                        <p class="client-service-name">
                            {{ internalContact.firstName }} {{ internalContact.lastName }}
                        </p>
                        <a [href]="phoneHref">{{ internalContact.phone }}</a>
                    </div>
                    <div>
                        <h4 i18n="Heading for client service address">Address</h4>
                        <p>315 E. Robinson St. Suite 200</p>
                        <p>Orlando, FL, 32801</p>
                    </div>
                    <div>
                        <h4 i18n="Heading for client service hours">Hours</h4>
                        <p>Monday - Friday</p>
                        <p>8:00am - 5:00pm EST</p>
                    </div>
                </div>
                <div class="contact-us-content-form">
                    <h4 i18n="Heading for contact form">Send us a message</h4>
                    <form [formGroup]="formGroup" (submit)="submitQuestionForm()">
                        <lsq-select
                            [label]="'I need help with...'"
                            [options]="helpOptions"
                            [group]="formGroup"
                            formControlName="helpType"
                        >
                        </lsq-select>
                        <lsq-textarea
                            [label]="'Message'"
                            [floatLabel]="true"
                            [group]="formGroup"
                            formControlName="helpMessage"
                            rows="10"
                        >
                        </lsq-textarea>
                        <lsq-button
                            type="submit"
                            [submitting]="submitting"
                            size="md"
                            [group]="formGroup"
                            i18n="Button used to submit the contact form"
                        >
                            Submit
                        </lsq-button>
                    </form>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent implements OnInit {
    public icons = Icons;
    public color = Color;
    public formGroup: FormGroup;
    public internalContact: Contact;
    public phoneHref: string;
    public submitting = false;
    public title = this._i18n({
        value: `Contact Us`,
        meaning: `Heading for the 'contact us' page`
    });

    public helpCenterItems = [
        {
            title: this._i18n({
                value: 'How to Upload Invoices',
                meaning: `Link to an article on how to upload invoices via LSQ`
            }),
            link:
                'https://lsq.zendesk.com/hc/en-us/articles/360022277373-How-do-I-submit-my-unpaid-invoices-'
        },
        {
            title: this._i18n({
                value: 'Where can I see Status of Fundings',
                meaning: `Link to an article on how to view the status of your LSQ fundings`
            }),
            link:
                'https://lsq.zendesk.com/hc/en-us/articles/360022246533-How-do-I-read-the-Last-30-Days-of-Funds-Request-report-'
        },
        {
            title: this._i18n({
                value: 'How do I Add New Users',
                meaning: `Link to an article on how to add users with your LSQ account`
            }),
            link:
                'https://lsq.zendesk.com/hc/en-us/articles/360022240993--How-do-I-add-new-users-to-Dashboard-'
        },
        {
            title: this._i18n({
                value: 'What are Ineligible Invoices',
                meaning: `Link to an article explaining what makes an invoice ineligible for factoring`
            }),
            link:
                'https://lsq.zendesk.com/hc/en-us/articles/360022023654-What-are-ineligible-invoices-'
        }
    ];

    public helpOptions: ISelectOption[] = [
        {
            value: HelpOption.InvoiceAndFundingStatus,
            label: this._i18n({
                // ngx-extractor doesn't let us access the enum here, so `value`s here are
                // hard-coded.
                value: 'Invoice and Funding Status',
                meaning: `Indicates that the message is regarding the user's funding status`
            })
        },
        {
            value: HelpOption.AgingInquiry,
            label: this._i18n({
                value: 'Aging Inquiry',
                meaning: `Indicates that the message is regarding an aging inquiry`
            })
        },
        {
            value: HelpOption.Pricing,
            label: this._i18n({
                value: 'Pricing',
                meaning: `Indicates that the message is regarding pricing`
            })
        },
        {
            value: HelpOption.TechnicalSupport,
            label: this._i18n({
                value: 'Technical Support',
                meaning: `Indicates that the message is regarding technical support`
            })
        },
        {
            value: HelpOption.Other,
            label: this._i18n({
                value: 'Other',
                meaning: `Indicates that the message is regarding a miscellaneous issue`
            })
        }
    ];

    constructor(
        private _alertService: AlertService,
        private _listInternalContactsForClientService: ListInternalContactsForClientService,
        private _sendInternalContactEmailService: SendInternalContactEmailService,
        private _i18n: I18n
    ) {
        this.setInternalContact(); // tslint:disable-line:no-floating-promises
    }

    public ngOnInit(): void {
        this.formGroup = new FormGroup({
            helpType: new FormControl('', [Validators.required]),
            helpMessage: new FormControl('', [Validators.required])
        });
    }

    public async setInternalContact(): Promise<void> {
        const response = await this._listInternalContactsForClientService.executeOnce(
            new ListInternalContactsForClientRequest()
        );
        this.internalContact = response.rows[0];
        this.phoneHref = `tel:${this.internalContact.phone}`;
    }

    public async submitQuestionForm(): Promise<void> {
        this.submitting = true;
        try {
            await this._sendInternalContactEmailService.executeOnce(
                new SendInternalContactEmailRequest({
                    requestType: HelpOptionForDisplay[this.formGroup.get('helpType').value],
                    requestText: this.formGroup.get('helpMessage').value
                })
            );
            this._alertService.success(
                this._i18n({
                    value: `We got your message, and you'll hear back from us shortly.`,
                    meaning: `Alerts the user that the contact form was submitted successfully`
                })
            );
            this.formGroup.reset();
        } catch {
            this._alertService.error(ErrorMessage.genericServerError);
        }
        this.submitting = false;
    }
}
